import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ThemeMode = 'light' | 'dark';
type Language = 'ru' | 'en';

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;

  // Theme
  themeMode: ThemeMode;

  // Language
  language: Language;

  // Modals
  activeModal: string | null;
  modalData: Record<string, unknown> | null;

  // Side Panel
  sidePanelOpen: boolean;
  sidePanelContent: string | null;
  sidePanelData: Record<string, unknown> | null;

  // Loading states
  globalLoading: boolean;

  // Assistant
  assistantOpen: boolean;
  assistantContext: Record<string, unknown> | null;
  assistantResetSession: boolean;
  assistantInitialPrompt: string | null;

  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;

  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;

  setLanguage: (lang: Language) => void;

  openModal: (modalId: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;

  openSidePanel: (content: string, data?: Record<string, unknown>) => void;
  closeSidePanel: () => void;

  setGlobalLoading: (loading: boolean) => void;

  openAssistant: (
    context?: Record<string, unknown>,
    options?: { resetSession?: boolean; initialPrompt?: string }
  ) => void;
  closeAssistant: () => void;
  clearAssistantContext: () => void;
}

export function mergePersistedUIState(
  persistedState: unknown,
  currentState: UIState
): UIState {
  const persisted = (persistedState || {}) as Partial<UIState>;

  return {
    ...currentState,
    sidebarCollapsed: typeof persisted.sidebarCollapsed === 'boolean'
      ? persisted.sidebarCollapsed
      : currentState.sidebarCollapsed,
    themeMode: persisted.themeMode === 'dark' || persisted.themeMode === 'light'
      ? persisted.themeMode
      : currentState.themeMode,
    language: persisted.language === 'ru' || persisted.language === 'en'
      ? persisted.language
      : currentState.language,
  };
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state
      sidebarCollapsed: false,
      sidebarMobileOpen: false,
      themeMode: 'light',
      language: 'ru',
      activeModal: null,
      modalData: null,
      sidePanelOpen: false,
      sidePanelContent: null,
      sidePanelData: null,
      globalLoading: false,
      assistantOpen: false,
      assistantContext: null,
      assistantResetSession: false,
      assistantInitialPrompt: null,

      // Sidebar actions
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleMobileSidebar: () => set((state) => ({ sidebarMobileOpen: !state.sidebarMobileOpen })),
      closeMobileSidebar: () => set({ sidebarMobileOpen: false }),

      // Theme actions
      setThemeMode: (mode) => set({ themeMode: mode }),
      toggleTheme: () => set((state) => ({
        themeMode: state.themeMode === 'light' ? 'dark' : 'light'
      })),

      // Language actions
      setLanguage: (lang) => set({ language: lang }),

      // Modal actions
      openModal: (modalId, data = {}) => set({
        activeModal: modalId,
        modalData: data
      }),
      closeModal: () => set({
        activeModal: null,
        modalData: null
      }),

      // Side Panel actions
      openSidePanel: (content, data = {}) => set({
        sidePanelOpen: true,
        sidePanelContent: content,
        sidePanelData: data
      }),
      closeSidePanel: () => set({
        sidePanelOpen: false,
        sidePanelContent: null,
        sidePanelData: null
      }),

      // Loading actions
      setGlobalLoading: (loading) => set({ globalLoading: loading }),

      // Assistant actions
      openAssistant: (context, options) => set({
        assistantOpen: true,
        assistantContext: context ?? null,
        assistantResetSession: options?.resetSession ?? false,
        assistantInitialPrompt: options?.initialPrompt ?? null,
      }),
      closeAssistant: () => set({ assistantOpen: false }),
      clearAssistantContext: () => set({
        assistantContext: null,
        assistantResetSession: false,
        assistantInitialPrompt: null,
      }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        themeMode: state.themeMode,
        language: state.language,
      }),
      merge: mergePersistedUIState,
    }
  )
);
